import React from "react";
import { ArrowRight, Award, Wheat, Flame } from "lucide-react";

const HERO_BG = "https://images.pexels.com/photos/11026056/pexels-photo-11026056.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

export default function Hero({ t }) {
  const scrollTo = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="top" className="relative min-h-[95vh] flex items-center overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover ken-burns"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/85 to-stone-950/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-transparent to-stone-950/30" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 pb-20">
        <div className="max-w-3xl reveal-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-600/40 bg-amber-600/10 text-amber-500 text-xs uppercase tracking-[0.25em] mb-8">
            <Award size={14} /> {t.eyebrow}
          </div>

          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.02] text-stone-50 tracking-tight">
            {t.h1_part1}{" "}
            <span className="italic text-amber-500">{t.h1_highlight}</span>{" "}
            {t.h1_part2}
          </h1>

          <p className="mt-8 text-lg sm:text-xl text-stone-300 max-w-2xl leading-relaxed">{t.sub}</p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#apply"
              onClick={(e) => scrollTo(e, "apply")}
              data-testid="hero-apply-btn"
              className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-stone-950 font-medium uppercase tracking-wider text-sm px-7 py-4 rounded-full btn-primary-glow"
            >
              {t.cta_primary} <ArrowRight size={16} />
            </a>
            <a
              href="#financials"
              onClick={(e) => scrollTo(e, "financials")}
              data-testid="hero-financials-btn"
              className="inline-flex items-center gap-2 border border-stone-700 hover:border-amber-600 hover:text-amber-500 text-stone-200 font-medium uppercase tracking-wider text-sm px-7 py-4 rounded-full transition-colors"
            >
              {t.cta_secondary}
            </a>
          </div>

          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl">
            <Stat number={t.stat_years_value || "135+"} label={t.stat_years} />
            <Stat number="100%" label={t.stat_ghee} icon={<Wheat size={14} />} />
            <Stat number="ODOC" label={t.stat_odoc} icon={<Award size={14} />} />
            <Stat number="04" label={t.stat_categories} icon={<Flame size={14} />} />
          </div>
        </div>
      </div>

      {/* scroll hint */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-stone-500 text-[10px] uppercase tracking-[0.3em]">
        <div className="w-px h-12 bg-gradient-to-b from-transparent to-amber-600 animate-pulse" />
        Scroll
      </div>
    </section>
  );
}

function Stat({ number, label, icon }) {
  return (
    <div className="border-l border-amber-600/40 pl-4">
      <div className="font-serif text-3xl text-stone-50">{number}</div>
      <div className="text-[11px] uppercase tracking-wider text-stone-400 mt-1 flex items-center gap-1.5">
        {icon}
        {label}
      </div>
    </div>
  );
}
